import { useContext, useEffect, useReducer, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import Collapse from 'react-bootstrap/Collapse';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getMenuList } from './Menu';
import { useWindowScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import { logoutAction } from '../../../store/actions/Auth/AuthActions';
import { PERMISSION_SLUGS, ROLE_SLUGS, userNeedsTransactionPin } from '../../../utils/auth/permissionConstants';
import { useSecurityModals } from '../../../context/SecurityModalsContext';
import { usePartnerHasSoloUser } from '../../../hooks/usePartnerHasSoloUser';
import './SideBar.css';

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
}

const SideBar = () => {
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
  } = useContext(ThemeContext);

  const [state, setState] = useReducer(reducer, initialState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.authUser);
  const userRole = authUser?.role_slug;
  const hasActiveWallet = authUser?.has_active_wallet;
  const hasActivePartnerVault = authUser?.has_active_partner_vault;
  const userPermissionSlugs = authUser?.all_permission_slugs || [];
  const currentPartner = authUser?.partner;
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await dispatch(logoutAction(navigate));
    } catch {
      toast.error('Logout failed. Please try again.');
      setIsLoggingOut(false);
    }
  };

  const hasVaultAdmin = userPermissionSlugs.includes(PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD) && hasActivePartnerVault;
  const { openChangePin, openForgotPin } = useSecurityModals();
  // The menu defaults to the team shape during the solo check load
  // window so team users see zero flicker on cold load; a solo user
  // briefly sees the team menu shape and then it flips when the check
  // resolves. Menu items are safe to briefly render for either class
  // since none carry money-movement consequence on click.
  const { hasSoloUser } = usePartnerHasSoloUser(authUser?.partner_id);
  const menuList = getMenuList({
    onLogout: handleLogout,
    onChangePin: openChangePin,
    onForgotPin: openForgotPin,
    hasActiveWallet,
    hasVaultAdmin,
    hasSoloUser,
    isExecAdmin: userRole === ROLE_SLUGS.EXEC_ADMIN,
  });

  const needsTransactionPin = userNeedsTransactionPin(authUser);

  const isItemVisible = (item) => {
    if (item.requiredPermissionSlug && !userPermissionSlugs.includes(item.requiredPermissionSlug)) return false;
    if (item.requiredRoleSlug && item.requiredRoleSlug !== userRole) return false;
    if (item.requiresPartner && !currentPartner) return false;
    if (item.requiresUserWallet && !hasActiveWallet) return false;
    if (item.requiresPartnerVault && !hasActivePartnerVault) return false;
    if (item.requiresTransactionPinCapability && !needsTransactionPin) return false;
    return true;
  };

  const filterMenuItems = (items) =>
    items
      .filter(isItemVisible)
      .map((item) => (item.content ? { ...item, content: filterMenuItems(item.content) } : item))
      .filter((item) => !item.content || item.content.length > 0);

  const filteredMenu = filterMenuItems(menuList);

  const [hideOnScroll, setHideOnScroll] = useState(true)
  useWindowScrollPosition(({ prevPos, currPos }) => {
    const isShow = currPos.y > prevPos.y
    if (isShow !== hideOnScroll) setHideOnScroll(isShow)
  })

  // Menu dropdown list 
  //const [active , setActive] = useState('');
  //const [activeSubmenu , setActiveSubmenu] = useState('');


  const handleMenuActive = status => {
    setState({ active: state.active === status ? "" : status });
  }
  const handleSubmenuActive = (status) => {
    setState({ activeSubmenu: state.activeSubmenu === status ? "" : status });
  }

  const location = useLocation();
  const activeSegment = location.pathname.split('/').filter(Boolean)[0] ?? '';

  const path = location.pathname.split('/').at(-1);

  const isRouteInsideGroup = (group, pathname) =>
    group.content?.some((child) => child.to && pathname.startsWith(child.to)) ?? false;

  useEffect(() => {
    // In any non-full sidebar layout (mini on tablet, overlay on mobile,
    // menu-toggle on desktop) the group's submenu is a floating popover.
    // Auto-opening it on route change would leave the popover pinned on top
    // of the main content until the user clicked the parent again. Keep the
    // popover transient there and only auto-open the group inline when the
    // sidebar is genuinely a full-width vertical rail.
    const sidebarStyle = document.body.getAttribute('data-sidebar-style');
    const mainWrapper = document.getElementById('main-wrapper');
    const isCollapsed = sidebarStyle !== 'full' || mainWrapper?.classList.contains('menu-toggle');
    if (isCollapsed) {
      setState({ active: "", activeSubmenu: "" });
      return;
    }
    const activeGroup = menuList.find((item) => item.content && isRouteInsideGroup(item, location.pathname));
    setState({ active: activeGroup?.title ?? "", activeSubmenu: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const closeDrawerIfMediumDevice = () => {
    if (window.innerWidth <= 768) {
      const sideBarDiv = document.getElementById('main-wrapper')

      if (sideBarDiv && sideBarDiv.classList.contains('menu-toggle')) {
        sideBarDiv.classList.remove('menu-toggle');
        const sidebarGridIcon = document.getElementById('side-menu-hambuger-reveal');

        if (sidebarGridIcon && sidebarGridIcon.classList.contains('is-active')) {
          sidebarGridIcon.classList.remove('is-active');
        }
      }
    }
  }

  return (
    <div
      className={`deznav  border-right ${iconHover} ${sidebarposition.value === "fixed" &&
        sidebarLayout.value === "horizontal" &&
        headerposition.value === "static"
        ? hideOnScroll > 120
          ? "fixed"
          : ""
        : ""
        }`}
    >
      <PerfectScrollbar className="deznav-scroll">
        <ul className="metismenu" id="menu">
          {filteredMenu.map((data, index) => {
            let menuClass = data.classsChange;

            if (menuClass === "menu-title") {
              return (
                <li className={menuClass} key={index} onClick={closeDrawerIfMediumDevice}>{data.title}</li>
              )
            } else {
              const isCollapsibleGroup = data.content && data.content.length > 0;
              return (
                <li className={`${menuClass ?? ''} ${state.active === data.title || (!data.isShortcut && '/' + activeSegment === data.to) ? 'mm-active' : ''}`}
                  key={index} onClick={isCollapsibleGroup ? undefined : closeDrawerIfMediumDevice}
                >

                  {data.content && data.content.length > 0 ?
                    <Link to={"#"}
                      className="has-arrow"
                      onClick={() => { handleMenuActive(data.title) }}
                      data-tour={data.dataTour}
                    >
                      {data.iconStyle}
                      <span className="nav-text">{data.title}</span>
                    </Link>
                    :
                    <NavLink to={data.to} data-tour={data.dataTour}>
                      {data.iconStyle}
                      <span className="nav-text">{data.title}</span>
                    </NavLink>
                  }
                  {data.content && data.content.length > 0 && (
                  <Collapse in={state.active === data.title ? true : false}>
                    <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                      {data.content.map((data, index) => {
                        return (
                          <li key={index}
                            className={`${state.activeSubmenu === data.title ? "mm-active" : ""}`}
                            onClick={() => {
                              closeDrawerIfMediumDevice();
                              setState({ active: "", activeSubmenu: "" });
                            }}
                          >
                            {data.content && data.content.length > 0 ?
                              <>
                                <NavLink to={data.to} className={data.hasMenu ? 'has-arrow' : ''}
                                  onClick={() => { handleSubmenuActive(data.title) }}
                                >
                                  {data.title}
                                </NavLink>
                                <Collapse in={state.activeSubmenu === data.title ? true : false}>
                                  <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                                    {data.content && data.content.map((data, index) => {
                                      return (
                                        <>
                                          <li key={index}>
                                            <Link className={`${'/' + path === data.to ? "mm-active" : ""}`} to={data.to}>{data.title}</Link>
                                          </li>
                                        </>
                                      )
                                    })}
                                  </ul>
                                </Collapse>
                              </>
                              : data.isAction ?
                              <a
                                href="#"
                                role="button"
                                className="sidebar-submenu-action"
                                data-tour={data.dataTour}
                                aria-disabled={data.title === 'Logout' && isLoggingOut ? 'true' : 'false'}
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (data.title === 'Logout' && isLoggingOut) return;
                                  data.onClick?.();
                                }}
                              >
                                {data.title === 'Logout' && isLoggingOut ? (
                                  <i className="fa fa-spinner fa-spin"></i>
                                ) : (
                                  data.title
                                )}
                              </a>
                              :
                              <NavLink to={data.to} data-tour={data.dataTour}>
                                {data.title}
                              </NavLink>
                            }

                          </li>

                        )
                      })}
                    </ul>
                  </Collapse>
                  )}
                </li>
              )
            }
          })}
        </ul>
      </PerfectScrollbar>
    </div>
  );
};

export default SideBar;
